import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { InfoPageComponent } from 'src/app/shared/info-page/info-page.component';
import { ZxGeofenceMapComponent } from '../zx-geofence-map/zx-geofence-map.component';
import { ConfirmDeleteDialogComponent } from '../confirm-delete-dialog/confirm-delete-dialog.component';
import { GeofenceService } from 'src/app/services/api/geofence.service';
import { Geofence } from 'src/app/models/geofence';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BackendComponent, ComponentsService } from 'src/app/services/api/components.service';
import { ComponentTypeService } from 'src/app/services/api/componentType.service';
import { ComponentType } from 'src/app/models/component-type';
import { SolutionSpace } from 'src/app/models/solution';
import { ComponentsSortOrder, SortDirection } from 'src/app/models/page-request';
import { ComponentState } from 'src/app/models/component';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { ServiceLocationDialogComponent } from '../service-location-dialog/service-location-dialog.component';
import { BreakpointService } from 'src/app/services/breakpoint.service';

@Component({
  selector: 'app-zx-geofence-overview',
  imports: [
    InfoPageComponent,
    MatButtonModule,
    SvgIconComponent,
    MatTabsModule,
    MatIconModule,
    MatTableModule,
    MatMenuModule,
    MatTooltipModule,
    ZxGeofenceMapComponent,
  ],
  templateUrl: './zx-geofence-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './zx-geofence-overview.component.scss',
})
export class ZxGeofenceOverviewComponent implements OnInit, OnDestroy {
  @ViewChild(ZxGeofenceMapComponent) mapComponent!: ZxGeofenceMapComponent;
  readonly bp = inject(BreakpointService);
  /** Below 1024px the list and the map take turns on the screen. */
  // Below 1024px the list is a drawer over the map (open first, as the list is how a geofence is picked).
  isDrawerOpen = true;
  private destroy$ = new Subject<void>();
  links = [
    { id: 'utilization', label: 'Utilization', path: '../utilization' },
    { id: 'humidity', label: 'Humidity', path: '../humidity' },
  ];
  activeLink = 'utilization';
  canAccess: string[] = [];
  dualSolutionAvailable: boolean = false;
  displayedColumns: string[] = ['name', 'description', 'actions'];
  isGeoFenceLoaded = false;
  solutionSpace: SolutionSpace = SolutionSpace.Utilization;
  dataSource: Geofence[] = [];
  geofences: Geofence[] = [];
  selectedGeofence: Geofence | null = null;
  componentsInGeofence: BackendComponent[] = [];
  isLoadingComponents: boolean = false;

  constructor(
    private geofenceService: GeofenceService,
    private titleService: Title,
    private authService: AuthenticationService,
    private componentService: ComponentsService,
    private componentTypeService: ComponentTypeService,
    private dialog: MatDialog,
    private snackBar: SnackbarService
  ) {}
  ngOnInit(): void {
    this.titleService.setTitle('GeoFence');

    this.authService.getUserAccessPermissions().subscribe((canAccess) => {
      this.canAccess = canAccess;
      this.dualSolutionAvailable = canAccess.includes('utilization') && canAccess.includes('humidity');
    });

    this.geofenceService.geofences$.pipe(takeUntil(this.destroy$)).subscribe((geofences) => {
      this.geofences = geofences;
      this.updateTableData();
    });

    // Initial load
    this.loadGeofences();
  }

  private loadGeofences(): void {
    this.geofenceService.getGeofences({ pageNumber: 1, pageSize: 1000 }).subscribe({
      next: () => {
        this.isGeoFenceLoaded = true;
      },
      error: () => {
        this.isGeoFenceLoaded = false;
        this.snackBar.openSnackBar(
          {
            title: 'Error',
            message: 'Error loading geofences',
          },
          'ERROR'
        );
      },
    });
  }

  private updateTableData(): void {
    this.dataSource = this.geofences;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRowClicked(geofence: Geofence): void {
    this.selectedGeofence = geofence;
    // Below 1024px the drawer covers the map: close it to show the picked geofence.
    if (this.bp.isCompact()) {
      this.isDrawerOpen = false;
    }

    this.loadComponentsInGeofence(geofence.id);

    if (this.mapComponent) {
      this.mapComponent.refreshSelectedGeofence();
    }
  }

  changeSolutionSpace(linkId: string): void {
    this.activeLink = linkId;

    this.solutionSpace = linkId === 'humidity' ? SolutionSpace.Humidity : SolutionSpace.Utilization;

    if (this.selectedGeofence) {
      this.loadComponentsInGeofence(this.selectedGeofence.id);
    }
  }

  private loadComponentsInGeofence(geofenceId: string): void {
    // Set loading state to true
    this.isLoadingComponents = true;

    const paginationParams = {
      pageNumber: 1,
      pageSize: 1000,
      sortDirection: SortDirection.ASC,
      sortOrder: ComponentsSortOrder.COMPONENT_NAME,
    };

    this.componentService
      .getComponents(this.solutionSpace, {
        pageRequest: paginationParams,
        geofenceIds: [geofenceId],
        states: [ComponentState.Available, ComponentState.InReturn, ComponentState.InService, ComponentState.InUse],
      })
      .subscribe({
        next: (response) => {
          this.componentsInGeofence = response.items;
          this.isLoadingComponents = false;
        },
        error: (error) => {
          console.error('Error loading components in geofence:', error);
          this.componentsInGeofence = [];
          this.isLoadingComponents = false;
          this.snackBar.openSnackBar(
            {
              title: 'Error',
              message: `Error loading components in geofence: ${error.message}`,
            },
            'ERROR'
          );
        },
      });
  }

  onDeleteClicked(geofence: Geofence, event: Event): void {
    event.stopPropagation(); // Prevent row click event

    this.geofenceService.getComponentTypesForGeofence(geofence.id).subscribe({
      next: (componentTypes: ComponentType[]) => {
        const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
          width: '450px',
          data: {
            title: 'Delete Geofence',
            message: `Are you sure you want to delete the geofence "${geofence.name}"?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            usedByComponentTypes: componentTypes.map((ct) => ct.name),
          },
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (!result) return;

          const removeRequests = componentTypes.map((ct) =>
            this.componentTypeService.removeGeofenceFromComponentType(ct.id, geofence.id)
          );

          const doDelete = () =>
            this.geofenceService.deleteGeofence(geofence.id).subscribe({
              next: () => {
                if (this.selectedGeofence?.id === geofence.id) {
                  this.selectedGeofence = null;
                  this.componentsInGeofence = [];
                  this.isLoadingComponents = false;
                }
              },
              error: () => {
                this.snackBar.openSnackBar(
                  { title: 'Error', message: 'Error deleting geofence. Please try again later.' },
                  'ERROR'
                );
              },
            });

          if (removeRequests.length > 0) {
            forkJoin(removeRequests).subscribe({
              next: () => doDelete(),
              error: () => {
                this.snackBar.openSnackBar(
                  { title: 'Error', message: 'Failed to remove geofence from component types.' },
                  'ERROR'
                );
              },
            });
          } else {
            doDelete();
          }
        });
      },
      error: () => {
        this.snackBar.openSnackBar(
          { title: 'Error', message: 'Failed to load component types for this geofence.' },
          'ERROR'
        );
      },
    });
  }

  toggleDrawer(): void {
    this.isDrawerOpen = !this.isDrawerOpen;
  }

  onClearSelection(): void {
    this.selectedGeofence = null;
    this.componentsInGeofence = [];
    this.isLoadingComponents = false;
  }

  onServiceLocationClicked(geofence: Geofence): void {
    this.dialog.open(ServiceLocationDialogComponent, {
      width: '600px',
      data: {
        geofence: geofence,
        solutionSpace: this.solutionSpace,
      },
    });
  }
}
